import React from 'react';

import { faEdit } from '@fortawesome/pro-regular-svg-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { FixedDurationTypeForm } from 'src/features/reservations/components/modals/FixedDurationTypeForm';
import {
    createFixedReservationTypeFormSchema,
    type CreateFixedReservationTypeFormSchemaValues,
} from 'src/features/reservations/hooks/useCreateReservationType';
import { useUpdateReservationType } from 'src/features/reservations/hooks/useUpdateReservationType';
import { reservationMessages } from 'src/features/reservations/utils/messages';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { toast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import { generalErrorMessages, generalMessages } from 'src/utils/messages';

import type { ReservationType } from 'src/features/reservations/types/reservationType';

type EditFixedDurationTypeModalProps = {
    isModalOpen: boolean;
    closeEditModal: (isOpen: boolean) => void;
    selectedType: ReservationType;
    refetch: () => void;
};

const EditFixedDurationTypeModal = ({
    isModalOpen,
    closeEditModal,
    selectedType,
    refetch,
}: EditFixedDurationTypeModalProps) => {
    const { formatMessage } = useIntl();
    const { parkingId } = useParams();

    const form = useForm<CreateFixedReservationTypeFormSchemaValues>({
        resolver: zodResolver(createFixedReservationTypeFormSchema),
        mode: 'onChange',
        defaultValues: {},
    });

    const { isDirty, isValid } = form.formState;

    React.useEffect(() => {
        if (selectedType) {
            form.reset({
                reservation_type: selectedType.reservation_type,
                valid_from: new Date(selectedType.valid_from),
                valid_to: selectedType.valid_to ? new Date(selectedType.valid_to) : undefined,
                price: selectedType.price,
                tax_amount: selectedType.tax_amount,
                tax_percent: selectedType.tax_percent,
                price_with_tax: selectedType.price_with_tax,
                currency: selectedType.currency,
                allowed_spaces: selectedType.allowed_spaces,
                entry_tolerance_minutes: selectedType.entry_tolerance_minutes,
                exit_tolerance_minutes: selectedType.exit_tolerance_minutes,
                reserve_space: selectedType.reserve_space,
                name: selectedType.name,
                description: selectedType.description,
                fixed_duration_start: selectedType.fixed_duration_start
                    ? new Date(selectedType.fixed_duration_start)
                    : undefined,
                fixed_duration_end: selectedType.fixed_duration_end
                    ? new Date(selectedType.fixed_duration_end)
                    : undefined,
            });
        }
    }, [selectedType]);

    const { mutate: editVersion } = useUpdateReservationType({
        onSuccess: () => {
            void refetch();
            toast({
                title: formatMessage(reservationMessages.editVersionSuccess),
                description: formatMessage(reservationMessages.editVersionSuccess),
            });
        },
        onError: (error) => {
            const errorData = error.response?.data;
            const errorCode = errorData?.code ?? formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorData?.message ?? formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });
    const { tax_percent, price_with_tax } = form.watch();

    const calculatedPrice = React.useMemo(() => {
        return price_with_tax ? Math.round(price_with_tax / (1 + Number(tax_percent))) : 0;
    }, [price_with_tax, tax_percent]);

    const taxAmountCalculation = React.useMemo(() => {
        return price_with_tax && calculatedPrice ? price_with_tax - calculatedPrice : 0;
    }, [price_with_tax, calculatedPrice]);

    const onSubmit = (values: CreateFixedReservationTypeFormSchemaValues) => {
        const dirtyFields = form.formState.dirtyFields;

        const editedFields = Object.keys(dirtyFields).reduce(
            (acc, key) => {
                const typedKey = key as keyof CreateFixedReservationTypeFormSchemaValues;
                acc[typedKey] = values[typedKey];
                return acc;
            },
            {} as Partial<Record<keyof CreateFixedReservationTypeFormSchemaValues, any>>,
        );
        if (dirtyFields.tax_percent || dirtyFields.price_with_tax) {
            editedFields.price = calculatedPrice;
            editedFields.tax_amount = taxAmountCalculation;
        }
        const submitData = {
            parkingId: parkingId || '',
            versionId: selectedType.id,
            ...editedFields,
        };
        editVersion(submitData);
        closeEditModal(!isModalOpen);
        form.reset();
    };

    return (
        <Dialog open={isModalOpen} onOpenChange={closeEditModal}>
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={faEdit} />
                        <div>
                            <DialogTitle>{formatMessage(reservationMessages.editVersion)}</DialogTitle>
                            <DialogDescription>
                                {formatMessage(reservationMessages.editVersionDetails)}
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <FixedDurationTypeForm form={form} onSubmit={form.handleSubmit(onSubmit)} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            {formatMessage(generalMessages.cancel)}
                        </Button>
                    </DialogClose>
                    <Button
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                        disabled={!isDirty || !isValid}
                    >
                        {formatMessage(reservationMessages.editVersion)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default EditFixedDurationTypeModal;
